import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Field, Spinner } from '../ui';
import { bankUploadApi, apiErrorMessage } from '../../services/api';
import { useOrg } from '../../context/OrgContext';
import { useToast } from '../../context/ToastContext';
import useEnrolledExams from '../../hooks/useEnrolledExams';
import { jobErrorMessage } from '../../utils/review';

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB

const PAPER_TYPES = [
  { value: 'custom_paper', label: 'Custom Paper / Practice Sheet / Mock Test' },
  { value: 'previous_year_paper', label: 'Previous Year Paper' }
];

export default function UploadBankDialog({ onClose, onUploaded }) {
  const org = useOrg();
  const toast = useToast();
  const navigate = useNavigate();
  const { exams, loading: examsLoading } = useEnrolledExams();

  const [examId, setExamId] = useState('');
  const [file, setFile] = useState(null);
  const [paperTitle, setPaperTitle] = useState('');
  const [paperType, setPaperType] = useState('custom_paper');
  const [paperYear, setPaperYear] = useState('');
  const [fileError, setFileError] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollError, setPollError] = useState(null);

  const pollTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Default to first enrolled exam once loaded
  useEffect(() => {
    if (exams?.length > 0 && !examId) {
      setExamId(exams[0].examId || exams[0].id);
    }
  }, [exams, examId]);

  // Clean up polling timer on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
    };
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    setFileError(null);

    if (!selected) {
      setFile(null);
      return;
    }

    if (!selected.name.toLowerCase().endsWith('.pdf') && selected.type !== 'application/pdf') {
      setFileError('Only PDF files are supported. Please choose a .pdf document.');
      setFile(null);
      return;
    }

    if (selected.size > MAX_FILE_SIZE_BYTES) {
      setFileError('File size exceeds the 50MB limit. Please choose a smaller PDF.');
      setFile(null);
      return;
    }

    setFile(selected);
    if (!paperTitle) {
      // Prepopulate title with filename minus extension
      const defaultTitle = selected.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      setPaperTitle(defaultTitle);
    }
  };

  const pollJob = (jobId) => {
    if (!org?.id || !jobId) return;

    bankUploadApi.getJob(org.id, jobId)
      .then((res) => {
        const job = res.data;
        setActiveJob(job);

        const status = (job?.status || '').toUpperCase();
        if (status === 'COMPLETED' || status === 'COMPLETED_WITH_ERRORS') {
          setPolling(false);
          toast.success(
            status === 'COMPLETED'
              ? 'Question paper processed successfully!'
              : 'Processing completed with some page warnings.'
          );
          if (onUploaded) onUploaded(job);
        } else if (status === 'ERROR' || status === 'FAILED' || status === 'UPLOAD_FAILED') {
          setPolling(false);
          setPollError(jobErrorMessage(job) || 'ETL processing failed. Please check the PDF format and try again.');
          toast.error('ETL extraction encountered an error.');
          if (onUploaded) onUploaded(job);
        } else {
          // Continue polling
          pollTimerRef.current = setTimeout(() => pollJob(jobId), 3000);
        }
      })
      .catch((err) => {
        setPollError(apiErrorMessage(err, 'Failed to fetch job status'));
        pollTimerRef.current = setTimeout(() => pollJob(jobId), 4000);
      });
  };

  const handleUpload = async (e) => {
    e?.preventDefault();
    if (!file) {
      setFileError('Please select a PDF question paper.');
      return;
    }
    if (!examId) {
      toast.error('Please select an enrolled exam.');
      return;
    }

    setUploading(true);
    setPollError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('exam_id', examId);
    if (paperTitle.trim()) formData.append('paper_title', paperTitle.trim());
    formData.append('paper_type', paperType || 'custom_paper');

    if (paperYear.trim()) {
      const parsedYear = parseInt(paperYear.trim(), 10);
      if (!isNaN(parsedYear)) {
        if (parsedYear < 1900 || parsedYear > 2099) {
          toast.error('Paper year must be between 1900 and 2099.');
          setUploading(false);
          return;
        }
        formData.append('paper_year', String(parsedYear));
      }
    }

    try {
      const res = await bankUploadApi.upload(org.id, formData);
      const createdJob = res.data;
      const jobId = createdJob?.jobId || createdJob?.id;
      setActiveJob(createdJob);
      setUploading(false);
      setPolling(true);
      if (jobId) {
        pollTimerRef.current = setTimeout(() => pollJob(jobId), 2500);
      }
    } catch (err) {
      setUploading(false);
      toast.error(apiErrorMessage(err, 'Failed to upload question paper'));
    }
  };

  const handleRetryInModal = async () => {
    const jobId = activeJob?.jobId || activeJob?.id;
    if (!jobId) return;
    setUploading(true);
    setPollError(null);
    try {
      await bankUploadApi.retryJob(org.id, jobId);
      toast.success('Extraction job restarted!');
      setUploading(false);
      setPolling(true);
      pollTimerRef.current = setTimeout(() => pollJob(jobId), 2500);
    } catch (err) {
      setUploading(false);
      toast.error(apiErrorMessage(err, 'Failed to retry extraction job'));
    }
  };

  const handleClose = () => {
    if (polling) {
      toast.success('Upload is processing in the background. Check your question banks list for updates.');
      if (onUploaded) onUploaded(activeJob);
    }
    onClose();
  };

  const handleReview = () => {
    const jobId = activeJob?.jobId || activeJob?.id;
    if (jobId) {
      onClose();
      navigate(`/question-banks/uploads/${jobId}`);
    }
  };

  // Render post-upload processing/results screen
  if (uploading || polling || (activeJob && !uploading && !polling)) {
    const status = (activeJob?.status || 'UPLOADED').toUpperCase();
    const isCompleted = status === 'COMPLETED' || status === 'COMPLETED_WITH_ERRORS';
    const isError = status === 'ERROR' || status === 'FAILED' || status === 'UPLOAD_FAILED';
    const phase = activeJob?.phase;
    const pagesProcessed = activeJob?.pagesProcessed ?? 0;
    const totalPages = activeJob?.totalPages ?? 0;
    const questionsWritten = activeJob?.totalQuestionsWritten ?? activeJob?.totalQuestionsExtracted ?? 0;
    const needsReviewCount = activeJob?.totalNeedsReview ?? 0;

    return (
      <Modal title={isCompleted ? 'Question Bank Ready' : isError ? 'Processing Failed' : 'Question Bank Processing'} onClose={handleClose}>
        <div className="upload-progress-modal">
          {!isCompleted && !isError ? (
            <div className="upload-status-box processing">
              <div className="status-spinner-wrap">
                <Spinner />
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 4px' }}>
                    {status === 'UPLOADED' ? 'Uploading & queued for extraction…' : `Processing PDF (${phase || 'Extracting'})`}
                  </h3>
                  <p className="muted" style={{ fontSize: 12 }}>
                    AI is analyzing questions, answer keys, diagrams, and topics from your document.
                  </p>
                </div>
              </div>

              <div className="progress-info-card">
                <div className="progress-info-row">
                  <span>Target Exam</span>
                  <b>{exams.find((e) => (e.examId || e.id) === examId)?.title || examId}</b>
                </div>
                <div className="progress-info-row">
                  <span>Document</span>
                  <b>{file?.name || activeJob?.fileName || 'Uploaded PDF'}</b>
                </div>
                {totalPages > 0 && (
                  <div className="progress-info-row">
                    <span>Pages Processed</span>
                    <b>{pagesProcessed} / {totalPages} pages</b>
                  </div>
                )}
                {phase && (
                  <div className="progress-info-row">
                    <span>Current Phase</span>
                    <span className="exam-pill">{phase}</span>
                  </div>
                )}
                {questionsWritten > 0 && (
                  <div className="progress-info-row">
                    <span>Questions Extracted</span>
                    <b style={{ color: 'var(--green)' }}>{questionsWritten} questions</b>
                  </div>
                )}
              </div>

              <p className="hint" style={{ marginTop: 8, textAlign: 'center' }}>
                You can safely close this window. Processing will continue in the background.
              </p>
            </div>
          ) : isCompleted ? (
            <div className="upload-status-box success">
              <div className="status-icon-circle success">✓</div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: '8px 0 4px' }}>
                {status === 'COMPLETED' ? 'Extraction Completed Successfully!' : 'Extraction Completed with Warnings'}
              </h3>
              <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
                Your question bank version has been created in <b>Draft</b> status. Review and verify questions before publishing.
              </p>

              <div className="metrics-compact">
                <div className="metric-pill">
                  <small>Questions Extracted</small>
                  <b>{questionsWritten}</b>
                </div>
                <div className="metric-pill">
                  <small>Needs Review</small>
                  <b style={{ color: needsReviewCount > 0 ? 'var(--amber)' : 'var(--green)' }}>
                    {needsReviewCount}
                  </b>
                </div>
                {activeJob?.totalValidationErrors > 0 && (
                  <div className="metric-pill">
                    <small>Validation Issues</small>
                    <b style={{ color: 'var(--red)' }}>{activeJob.totalValidationErrors}</b>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="upload-status-box error">
              <div className="status-icon-circle error">✕</div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: '8px 0 4px' }}>Processing Failed</h3>
              <p className="muted" style={{ fontSize: 13, color: 'var(--red)' }}>
                {pollError || jobErrorMessage(activeJob) || 'An error occurred while extracting questions from this PDF.'}
              </p>
            </div>
          )}

          <div className="modal-actions" style={{ marginTop: 20 }}>
            <Button variant="secondary" onClick={handleClose}>
              {isCompleted ? 'Close' : 'Run in background'}
            </Button>
            {isError && (
              <Button onClick={handleRetryInModal} disabled={uploading}>
                {uploading ? 'Restarting…' : 'Retry Extraction ↺'}
              </Button>
            )}
            {isCompleted && (
              <Button onClick={handleReview}>
                Review & Publish Questions →
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Upload Question Paper (PDF)" onClose={onClose}>
      <form className="form" onSubmit={handleUpload}>
        <Field label="Enrolled Exam" hint="Questions extracted will be added to this exam's question bank.">
          {examsLoading ? (
            <div className="muted"><Spinner /> Loading enrolled exams…</div>
          ) : !exams?.length ? (
            <div className="login-error">No enrolled exams found. Please enroll in an exam from the Exam Library first.</div>
          ) : (
            <select value={examId} onChange={(e) => setExamId(e.target.value)} required>
              {exams.map((exam) => (
                <option key={exam.id || exam.examId} value={exam.examId || exam.id}>
                  {exam.title || exam.name || exam.shortName}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="PDF Document" hint="Maximum file size: 50MB. PDF with readable text and diagrams.">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            required
          />
          {file && (
            <span className="file-selected-badge">
              📄 {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
            </span>
          )}
          {fileError && <span className="field-error-text">{fileError}</span>}
        </Field>

        <Field label="Paper Title" hint="Display name for this paper or question bank version.">
          <input
            type="text"
            placeholder="e.g. JEE Main 2024 Session 1 Physics & Chemistry"
            value={paperTitle}
            onChange={(e) => setPaperTitle(e.target.value)}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Paper Type (Optional)">
            <select value={paperType} onChange={(e) => setPaperType(e.target.value)}>
              {PAPER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Paper Year (Optional)">
            <input
              type="text"
              placeholder="e.g. 2024"
              value={paperYear}
              onChange={(e) => setPaperYear(e.target.value)}
            />
          </Field>
        </div>

        <div className="upload-notice-box">
          <p>
            <strong>ℹ️ How it works:</strong> The PDF will be split and processed by Gemini Vision to extract questions, answer choices, correct answers, explanations, and hierarchy topics into a new <em>Draft</em> bank version.
          </p>
        </div>

        <div className="modal-actions">
          <Button type="button" variant="secondary" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button type="submit" disabled={!file || !examId || uploading || !!fileError}>
            {uploading ? 'Starting upload…' : 'Upload & Extract Questions'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
